import React from 'react';
import { LightbulbIcon } from '@bizfirst/common-react';
import {
  ExecutionObserverConfig,
  EventTypeFilter,
  DEFAULT_EVENT_TYPE_FILTER,
} from '../../models/ExecutionObserverConfig';
import '../../styles/tabs/EventFiltersTab.css';

interface EventFiltersTabProps {
  config: ExecutionObserverConfig;
  onChange: (config: ExecutionObserverConfig) => void;
  readonly?: boolean;
}

export const EventFiltersTab: React.FC<EventFiltersTabProps> = ({
  config,
  onChange,
  readonly = false
}) => {
  const filter = config.filter || {};
  const eventTypes = filter.eventTypes || DEFAULT_EVENT_TYPE_FILTER;

  const updateEventTypes = (updates: Partial<EventTypeFilter>) => {
    onChange({
      ...config,
      filter: {
        ...filter,
        eventTypes: { ...eventTypes, ...updates }
      }
    });
  };

  const isErrorsOnly = eventTypes.errorsOnly || false;

  return (
    <fieldset className="event-filters-tab" disabled={readonly}>
      <legend>Event Type Filters</legend>

      {/* Errors Only Toggle */}
      <div className="form-group" style={{ marginTop: '12px' }}>
        <label className="checkbox-label highlight">
          <input
            type="checkbox"
            checked={isErrorsOnly}
            onChange={(e) => updateEventTypes({ errorsOnly: e.target.checked })}
            disabled={readonly}
          />
          <span className="checkbox-text">Errors Only</span>
        </label>
        <small className="help-text">
          When enabled, ignores all other event type filters and shows only error events
        </small>
      </div>

      {/* Event Type Checkboxes */}
      <div className="form-group">
        <label>Event Types:</label>
        <div className="event-types-grid">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={eventTypes.includeStarting ?? true}
              onChange={(e) => updateEventTypes({ includeStarting: e.target.checked })}
              disabled={readonly || isErrorsOnly}
            />
            <span>Starting</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={eventTypes.includeExecuting ?? true}
              onChange={(e) => updateEventTypes({ includeExecuting: e.target.checked })}
              disabled={readonly || isErrorsOnly}
            />
            <span>Executing</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={eventTypes.includeCompleted ?? true}
              onChange={(e) => updateEventTypes({ includeCompleted: e.target.checked })}
              disabled={readonly || isErrorsOnly}
            />
            <span>Completed</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={eventTypes.includePaused ?? true}
              onChange={(e) => updateEventTypes({ includePaused: e.target.checked })}
              disabled={readonly || isErrorsOnly}
            />
            <span>Paused</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={eventTypes.includeErrored ?? true}
              onChange={(e) => updateEventTypes({ includeErrored: e.target.checked })}
              disabled={readonly}
            />
            <span>Errored</span>
          </label>
        </div>
      </div>

      <div className="info-box">
        <p>
          <LightbulbIcon size={14} /> <strong>Tip:</strong> Use "Errors Only" mode to focus on problems during execution.
        </p>
      </div>
    </fieldset>
  );
};

export default EventFiltersTab;
